import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import RichTextRenderer from '../common/RichTextRenderer';

/**
 * ReinforcementPopup Component
 * Small centered modal that displays reinforcement content
 * Automatically appears when advancing to reinforcement, closes to mark complete and advance
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether popup is visible
 * @param {Object} props.content - Reinforcement content object with title and content
 * @param {Function} props.onClose - Callback when close button is clicked (marks complete and advances)
 */
const ReinforcementPopup = ({ isOpen, content, onClose }) => {
  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when popup is open (mobile improvement)
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !content) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-40 z-[10000] flex items-center justify-center p-3 sm:p-4 animate-fadeIn"
      onClick={onClose}
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[95vw] sm:max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xl sm:text-2xl">💡</span>
            </div>
            {/* <div className="min-w-0">
              <h3 className="text-white font-bold text-base sm:text-lg truncate">Reinforcement</h3>
            </div> */}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 active:bg-opacity-40 text-white transition-all duration-200 hover:scale-110 active:scale-95 ml-2 flex-shrink-0"
            aria-label="Close reinforcement popup"
            title="Close and continue"
          >
            <FaTimes className="text-base sm:text-lg" />
          </button>
        </div>

        {/* Content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6 min-h-0">
          {/* Content title */}
          <h4 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-teal-200">
            {content.title}
          </h4>

          {/* Rich text content */}
          <RichTextRenderer
            content={content.content}
            className="text-gray-700 reinforcement-content"
            fontSize="15px"
            lineHeight="1.7"
            allowInlineStyles={true}
          />
        </div>

        {/* Footer with helpful info - uncomment if needed */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 bg-teal-50 border-t border-teal-100 flex-shrink-0">
          <p className="text-teal-600 text-xs sm:text-sm text-center">
            Close to continue to next content
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }

        /* Custom scrollbar for content */
        .reinforcement-content::-webkit-scrollbar {
          width: 8px;
        }

        .reinforcement-content::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .reinforcement-content::-webkit-scrollbar-thumb {
          background: #14b8a6;
          border-radius: 4px;
        }

        .reinforcement-content::-webkit-scrollbar-thumb:hover {
          background: #0d9488;
        }

        /* Mobile touch improvements */
        @media (max-width: 640px) {
          .reinforcement-content {
            -webkit-overflow-scrolling: touch;
          }
        }

        /* Prevent zoom on double-tap for close button on mobile */
        button {
          touch-action: manipulation;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ReinforcementPopup;
